import { createClient, type EdgeConfigClient } from '@vercel/edge-config';

import { type EdgeConfigSettings, getEdgeConfiguration } from '../config';
import type {
  EdgeConfigFlags,
  EdgeConfigItems,
  Environment,
  FeatureFlags,
  IEdgeConfigService,
} from '../types';

export class EdgeConfigService implements IEdgeConfigService {
  private readonly client: EdgeConfigClient | undefined;
  private readonly vercelToken: string | undefined;
  private readonly edgeConfigId: string | undefined;
  private readonly teamId: string | undefined;
  private readonly environment: Environment;
  private readonly isConfigured: boolean;

  constructor(config?: Partial<EdgeConfigSettings>) {
    this.environment = (process.env.NODE_ENV === 'production' ? 'production' : 'development') as Environment;
    
    try {
      const settings = getEdgeConfiguration(config);
      this.vercelToken = settings.vercelToken;
      this.edgeConfigId = settings.edgeConfigId;
      this.teamId = settings.teamId;
      this.client = createClient(settings.edgeConfig);
      this.isConfigured = true;
    } catch (error) {
      if (this.environment === 'development') {
        console.warn('EdgeConfigService: Edge Config not configured. Feature flags will return false in development.');
        this.isConfigured = false;
      } else {
        throw error;
      }
    }
  }

  async getItem(key: string): Promise<boolean | null> {
    if (!this.isConfigured || !this.client) {
      return null;
    }
    
    try {
      const flags = await this.client.get<EdgeConfigItems>('flags');
      return flags?.[this.environment]?.[key] ?? null;
    } catch (error) {
      console.error(`Error fetching Edge Config item ${key}:`, error);
      return null;
    }
  }

  async getAllItems(): Promise<FeatureFlags> {
    if (!this.isConfigured || !this.client) {
      return {} as FeatureFlags;
    }
    
    try {
      const flags = await this.client.get<EdgeConfigItems>('flags');
      return flags?.[this.environment] ?? {} as FeatureFlags;
    } catch (error) {
      console.error('Error fetching all Edge Config items:', error);
      return {} as FeatureFlags;
    }
  }

  async getItemUsingAPI(): Promise<EdgeConfigFlags> {
    if (!this.isConfigured || !this.vercelToken || !this.edgeConfigId || !this.teamId) {
      return {} as EdgeConfigFlags;
    }
    
    try {
      const response = await fetch(
        `https://api.vercel.com/v1/edge-config/${this.edgeConfigId}/item/flags?teamId=${this.teamId}`,
        {
          method: 'GET',
          headers: {
            Authorization: `Bearer ${this.vercelToken}`,
          },
        },
      );

      if (!response.ok) {
        throw new Error(`Failed to fetch items: ${await response.text()}`);
      }

      const data = await response.json();
      return (data.value ?? {}) as EdgeConfigFlags;
    } catch (error) {
      console.error('Error fetching Edge Config items using API:', error);
      return {} as EdgeConfigFlags;
    }
  }

  async updateItem(key: string, value: boolean): Promise<void> {
    if (!this.isConfigured || !this.vercelToken || !this.edgeConfigId || !this.teamId) {
      console.warn('EdgeConfigService: Cannot update item - service not configured');
      return;
    }
    
    try {
      const flags = await this.getItemUsingAPI();

      // Update the environment-specific flag
      const updatedFlags: EdgeConfigFlags = {
        ...flags,
        [this.environment]: {
          ...(flags[this.environment] ?? {}),
          [key]: value,
        },
      } as EdgeConfigFlags;

      const response = await fetch(
        `https://api.vercel.com/v1/edge-config/${this.edgeConfigId}/items?teamId=${this.teamId}`,
        {
          method: 'PATCH',
          headers: {
            Authorization: `Bearer ${this.vercelToken}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            items: [
              {
                operation: 'upsert',
                key: 'flags',
                value: updatedFlags,
              },
            ],
          }),
        },
      );

      if (!response.ok) {
        throw new Error(`Failed to update item: ${await response.text()}`);
      }
    } catch (error) {
      console.error(`Error updating Edge Config item ${key}:`, error);
      throw error;
    }
  }

  async deleteItem(key: string): Promise<void> {
    if (!this.isConfigured || !this.vercelToken || !this.edgeConfigId || !this.teamId) {
      console.warn('EdgeConfigService: Cannot delete item - service not configured');
      return;
    }
    
    try {
      const flags = await this.getItemUsingAPI();

      // Remove the environment-specific flag
      if (flags[this.environment]) {
        const envFlags = { ...flags[this.environment] };
        delete envFlags[key];
        const updatedFlags: EdgeConfigFlags = {
          ...flags,
          [this.environment]: envFlags,
        } as EdgeConfigFlags;

        const response = await fetch(
          `https://api.vercel.com/v1/edge-config/${this.edgeConfigId}/items?teamId=${this.teamId}`,
          {
            method: 'PATCH',
            headers: {
              Authorization: `Bearer ${this.vercelToken}`,
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              items: [
                {
                  operation: 'upsert',
                  key: 'flags',
                  value: updatedFlags,
                },
              ],
            }),
          },
        );

        if (!response.ok) {
          throw new Error(`Failed to delete item: ${await response.text()}`);
        }
      }
    } catch (error) {
      console.error(`Error deleting Edge Config item ${key}:`, error);
      throw error;
    }
  }
}
